import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  ArrowDownTrayIcon,
  ArrowTopRightOnSquareIcon,
  ComputerDesktopIcon,
  PrinterIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import type { MailAttachment } from '../../types/mail.types';
import {
  fetchAttachmentPreviewFile,
  requestAttachmentPreview,
} from '../../services/MailClientAPI';
import { PdfViewer } from '@/shared/components/PdfViewer';

interface AttachmentPreviewModalProps {
  open: boolean;
  attachments: MailAttachment[];
  initialAttachmentId: string;
  onClose: () => void;
  onOpenInDms?: (attachment: MailAttachment) => Promise<void>;
  onSaveAsToDms?: (attachment: MailAttachment) => Promise<void>;
  onSaveLocal?: (attachment: MailAttachment) => Promise<void>;
}

type PreviewStatus = 'idle' | 'loading' | 'processing' | 'ready' | 'unsupported' | 'error';

type PreviewState = {
  status: PreviewStatus;
  previewType?: 'pdf' | 'image' | 'text';
  objectUrl?: string;
  text?: string;
  pdfData?: Uint8Array;
  error?: string;
};

export default function AttachmentPreviewModal({
  open,
  attachments,
  initialAttachmentId,
  onClose,
  onOpenInDms,
  onSaveAsToDms,
  onSaveLocal,
}: AttachmentPreviewModalProps) {
  const [activeId, setActiveId] = useState(initialAttachmentId);
  const [previewState, setPreviewState] = useState<PreviewState>({ status: 'idle' });
  const [dmsAction, setDmsAction] = useState<'open' | 'save' | null>(null);
  const [localSaveRunning, setLocalSaveRunning] = useState(false);
  const [dmsError, setDmsError] = useState<string | null>(null);
  const pollTimeout = useRef<number | null>(null);
  const attemptsRef = useRef(0);

  const activeAttachment = useMemo(
    () => attachments.find((att) => att.id === activeId) || attachments[0],
    [attachments, activeId],
  );

  useEffect(() => {
    if (open) {
      setActiveId(initialAttachmentId);
    }
  }, [open, initialAttachmentId]);

  useEffect(() => {
    return () => {
      if (pollTimeout.current) window.clearTimeout(pollTimeout.current);
      if (previewState.objectUrl) URL.revokeObjectURL(previewState.objectUrl);
    };
  }, [previewState.objectUrl]);

  useEffect(() => {
    if (!open || !activeAttachment) return;

    let cancelled = false;

    const loadPreview = async () => {
      setPreviewState({ status: 'loading' });
      attemptsRef.current = 0;

      const run = async () => {
        if (cancelled) return;
        try {
          const response = await requestAttachmentPreview(activeAttachment.id);
          if (cancelled) return;

          if (response.status === 'processing') {
            setPreviewState({ status: 'processing', previewType: response.previewType });
            attemptsRef.current += 1;
            if (attemptsRef.current < 10) {
              pollTimeout.current = window.setTimeout(run, 2000);
            } else {
              setPreviewState({ status: 'error', error: 'Preview is taking longer than expected.' });
            }
            return;
          }

          if (response.status === 'unsupported') {
            setPreviewState({ status: 'unsupported', error: response.message || 'Preview not available.' });
            return;
          }

          if (response.status === 'failed') {
            setPreviewState({ status: 'error', error: response.message || 'Preview generation failed.' });
            return;
          }

          const previewType = response.previewType || 'pdf';
          if (previewType === 'pdf') {
            const data = await fetchAttachmentPreviewFile<ArrayBuffer>(activeAttachment.id, 'arraybuffer');
            const buffer = new Uint8Array(data);
            setPreviewState({ status: 'ready', previewType: 'pdf', pdfData: buffer });
            return;
          }

          if (previewType === 'image') {
            const blob = await fetchAttachmentPreviewFile<Blob>(activeAttachment.id, 'blob');
            const objectUrl = URL.createObjectURL(blob);
            setPreviewState({ status: 'ready', previewType: 'image', objectUrl });
            return;
          }

          if (previewType === 'text') {
            const text = await fetchAttachmentPreviewFile<string>(activeAttachment.id, 'text');
            setPreviewState({ status: 'ready', previewType: 'text', text });
            return;
          }

          setPreviewState({ status: 'unsupported', error: 'Preview not available for this file.' });
        } catch (error) {
          if (cancelled) return;
          setPreviewState({ status: 'error', error: error instanceof Error ? error.message : 'Preview failed.' });
        }
      };

      await run();
    };

    loadPreview();

    return () => {
      cancelled = true;
      if (pollTimeout.current) window.clearTimeout(pollTimeout.current);
    };
  }, [open, activeAttachment?.id]);

  // Print the currently-rendered preview via a hidden iframe so the dialog
  // captures only the attachment, not the EdgeMail chrome behind it. PDFs
  // load by blob URL (browsers know how to print them); images and text are
  // wrapped in minimal HTML.
  const handlePrintPreview = () => {
    if (previewState.status !== 'ready') return;
    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = '0';
    iframe.setAttribute('aria-hidden', 'true');
    document.body.appendChild(iframe);

    let blobUrl: string | null = null;
    const cleanup = () => {
      if (blobUrl) URL.revokeObjectURL(blobUrl);
      iframe.remove();
    };

    const triggerPrint = () => {
      try {
        iframe.contentWindow?.focus();
        iframe.contentWindow?.print();
      } catch {
        cleanup();
      }
      // Some browsers don't fire afterprint on the iframe; clean up anyway.
      window.setTimeout(cleanup, 5000);
    };

    if (previewState.previewType === 'pdf' && previewState.pdfData) {
      const blob = new Blob([previewState.pdfData], { type: 'application/pdf' });
      blobUrl = URL.createObjectURL(blob);
      iframe.src = blobUrl;
      iframe.addEventListener('load', triggerPrint, { once: true });
      return;
    }

    const escape = (s: string) =>
      s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

    const doc = iframe.contentWindow?.document;
    if (!doc) {
      cleanup();
      return;
    }

    if (previewState.previewType === 'image' && previewState.objectUrl) {
      doc.open();
      doc.write(`<!doctype html><html><head><title>${escape(activeAttachment.filename)}</title>
        <style>html,body{margin:0;padding:0}img{max-width:100%;height:auto;display:block;margin:0 auto}</style>
        </head><body><img src="${previewState.objectUrl}" onload="window.focus();window.print();" /></body></html>`);
      doc.close();
      window.setTimeout(cleanup, 5000);
      return;
    }

    if (previewState.previewType === 'text') {
      doc.open();
      doc.write(`<!doctype html><html><head><title>${escape(activeAttachment.filename)}</title>
        <style>body{font:12pt/1.4 ui-monospace,Consolas,monospace;white-space:pre-wrap;word-wrap:break-word;padding:24px;margin:0}</style>
        </head><body>${escape(previewState.text || '')}</body></html>`);
      doc.close();
      triggerPrint();
      return;
    }

    cleanup();
  };

  const canPrintPreview =
    previewState.status === 'ready' &&
    (previewState.previewType === 'pdf' ||
      previewState.previewType === 'image' ||
      previewState.previewType === 'text');

  const runDmsAction = async (
    action: 'open' | 'save',
    handler: ((attachment: MailAttachment) => Promise<void>) | undefined,
  ) => {
    if (!handler || !activeAttachment) return;
    setDmsAction(action);
    setDmsError(null);
    let succeeded = false;
    try {
      await handler(activeAttachment);
      succeeded = true;
    } catch (error) {
      setDmsError(error instanceof Error ? error.message : 'Unable to send attachment to DMS.');
    } finally {
      setDmsAction(null);
    }
    if (succeeded && action === 'save') {
      onClose();
    }
  };

  const runLocalSaveAction = async () => {
    if (!activeAttachment || !onSaveLocal || localSaveRunning) return;
    setDmsError(null);
    setLocalSaveRunning(true);
    try {
      await onSaveLocal(activeAttachment);
    } catch (error) {
      setDmsError(error instanceof Error ? error.message : 'Unable to save attachment locally.');
    } finally {
      setLocalSaveRunning(false);
    }
  };

  if (!open || !activeAttachment) return null;

  const modal = (
    <div className="fixed inset-0 z-[80] flex items-center justify-center" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative w-[72vw] h-[70vh] max-w-4xl bg-[var(--em-surface-card)] border border-[var(--em-border-primary)] rounded-2xl shadow-2xl flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3 border-b border-[var(--em-border-primary)]">
          <div>
            <div className="text-sm font-semibold text-[var(--em-text-primary)]">Preview</div>
            <div className="text-xs text-[var(--em-text-tertiary)]">{activeAttachment.filename}</div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => runDmsAction('open', onOpenInDms)}
              disabled={!onOpenInDms || dmsAction !== null || localSaveRunning}
              className="flex items-center gap-1 px-3 py-1.5 text-xs rounded-full border border-[var(--em-border-secondary)] text-[var(--em-text-secondary)] hover:bg-[var(--em-state-hover)]"
            >
              <ArrowTopRightOnSquareIcon className="w-4 h-4" />
              {dmsAction === 'open' ? 'Opening...' : 'Open'}
            </button>
            <button
              type="button"
              onClick={() => runDmsAction('save', onSaveAsToDms)}
              disabled={!onSaveAsToDms || dmsAction !== null || localSaveRunning}
              className="flex items-center gap-1 px-3 py-1.5 text-xs rounded-full bg-[var(--em-accent-primary)] text-white hover:opacity-90"
            >
              <ArrowDownTrayIcon className="w-4 h-4" />
              {dmsAction === 'save' ? 'Preparing...' : 'Save to EdgeDocs'}
            </button>
            <button
              type="button"
              onClick={runLocalSaveAction}
              disabled={!onSaveLocal || localSaveRunning || dmsAction !== null}
              className="flex items-center gap-1 px-3 py-1.5 text-xs rounded-full border border-[var(--em-border-secondary)] text-[var(--em-text-secondary)] hover:bg-[var(--em-state-hover)] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ComputerDesktopIcon className="w-4 h-4" />
              {localSaveRunning ? 'Saving...' : 'Save Local'}
            </button>
            <button
              type="button"
              onClick={handlePrintPreview}
              disabled={!canPrintPreview}
              title={canPrintPreview ? 'Print this attachment' : 'Preview not ready'}
              className="flex items-center gap-1 px-3 py-1.5 text-xs rounded-full border border-[var(--em-border-secondary)] text-[var(--em-text-secondary)] hover:bg-[var(--em-state-hover)] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <PrinterIcon className="w-4 h-4" />
              Print
            </button>
            <button
              onClick={onClose}
              className="p-1 text-[var(--em-text-tertiary)] hover:text-[var(--em-text-primary)] rounded hover:bg-[var(--em-state-hover)] transition-colors"
            >
              <XMarkIcon className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="flex-1 min-h-0 p-3 overflow-hidden bg-[var(--em-surface-card)]">
          {dmsError && (
            <div className="mb-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
              {dmsError}
            </div>
          )}
          {previewState.status === 'loading' && (
            <div className="h-full flex items-center justify-center text-sm text-[var(--em-text-tertiary)]">
              Preparing preview…
            </div>
          )}
          {previewState.status === 'processing' && (
            <div className="h-full flex flex-col items-center justify-center gap-2 text-sm text-[var(--em-text-tertiary)]">
              <div className="h-1 w-48 rounded-full bg-[var(--em-border-subtle)] overflow-hidden">
                <div className="h-full w-1/2 bg-[var(--em-accent-primary)] animate-pulse" />
              </div>
              Generating preview…
            </div>
          )}
          {previewState.status === 'unsupported' && (
            <div className="h-full flex items-center justify-center text-sm text-[var(--em-text-tertiary)]">
              {previewState.error || 'Preview not available for this attachment.'}
            </div>
          )}
          {previewState.status === 'error' && (
            <div className="h-full flex items-center justify-center text-sm text-red-500">
              {previewState.error || 'Unable to load preview.'}
            </div>
          )}
          {previewState.status === 'ready' && previewState.previewType === 'pdf' && previewState.pdfData && (
            <PdfViewer
              data={previewState.pdfData}
              surfaceClassName="bg-[var(--em-surface-app)] border-[var(--em-border-secondary)]"
            />
          )}
          {previewState.status === 'ready' && previewState.previewType === 'image' && previewState.objectUrl && (
            <div className="h-full flex items-center justify-center bg-[var(--em-surface-app)] rounded-xl border border-[var(--em-border-secondary)]">
              <img
                src={previewState.objectUrl}
                alt={activeAttachment.filename}
                className="max-h-full max-w-full"
              />
            </div>
          )}
          {previewState.status === 'ready' && previewState.previewType === 'text' && (
            <div className="h-full overflow-auto rounded-xl border border-[var(--em-border-secondary)] bg-[var(--em-surface-app)] p-4">
              <pre className="text-xs text-[var(--em-text-primary)] whitespace-pre-wrap break-words">
                {previewState.text || ''}
              </pre>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  if (typeof document === 'undefined') return null;
  return createPortal(modal, document.body);
}
